import { HttpClient } from "@angular/common/http";
import { computed, inject, Injectable, Signal, signal } from "@angular/core";
import { BasketService } from "./basket.service";
import { catchError, combineLatest, map, of, Subject, switchMap, tap } from "rxjs";
import { Checkout, CheckoutPostItem, ConfirmCheckoutDto, PaymentStatusEnum, TicketConfig, UpdateCheckoutItemsDto, UpdateCheckoutVouchersDto } from "@core/models";
import { AppConfigService } from "@core/services/config.service";
import { PaymentGatewayService } from "./payment-gateways";
import { PaymentProcessorResponse, PaymentRequest } from "@core/models/payment";

@Injectable()
export class CheckoutService {
  checkout = signal<Checkout | null>(null);
  processorId = signal<string | undefined>(undefined);
  checkoutTotalPrice: Signal<number> = computed(() => this.checkout()?.realPrice ?? 0);
  confirmCheckOutRes = signal<any | null>(null);
  confirmPaymentRes = signal<any>(null);

  private readonly paymentStatus: Subject<string> = new Subject<string>();
  $paymentStatus = this.paymentStatus.asObservable();

  private readonly http = inject(HttpClient);
  private readonly basketService = inject(BasketService);
  private readonly appConfigService = inject(AppConfigService);
  private readonly paymentGateway = inject(PaymentGatewayService);

  get checkoutBody() {
    const basket = this.basketService.basket()
    const shopID = basket?.event?.underShops?.find(underShop => underShop.name == "Kiosk")?._id;

    return {
      type: "transaction",
      eventId: basket?.event?._id,
      shopId: shopID,
      salesChannelId: this.appConfigService.currentConfig().salechannelId,
      items: Object.keys(basket?.tickets ?? {}).map((key) => {
        return basket?.tickets?.[key].map((ticketConfig: TicketConfig) => {
          const _item: CheckoutPostItem & { type: string } = {
            amount: ticketConfig.quantity,
            ticketTypeId: ticketConfig.ticket.id,
            type: "ticket"
          };
          if (ticketConfig.timeslot) {
            _item.slotInfo = {
              slotId: key,
              slotStartTime: `${ticketConfig.timeslot.startTime.hour}:${ticketConfig.timeslot.startTime.minute == 0 ? '00' : ticketConfig.timeslot.startTime.minute}`
            };
          }
          const _items = [_item];
          const additionalTicketCount = this.basketService.getAdditionalTicketCount(ticketConfig.ticket.id);
          for (let i = 0; i < additionalTicketCount; i++) {
            _items.push(_item);
          }
          return _items;
        }).flat()
      }).flat()
    };
  }

  doCheckout() {
    if (!this.basketService.basket()) return of(null);
    return this.http.post(`${this.appConfigService.host()}/checkout`, this.checkoutBody);
  }

  udpateCheckout() {
    if (!this.checkout()) return of(null);
    return this.http.post(`${this.appConfigService.host()}/checkout/${this.checkout()!._id}/items`, {
      secret: this.checkout()!.secret,
      eventId: this.basketService.basket()?.event._id,
      shopId: this.checkout()?.items?.[0]?.shopId,
      items: this.checkout()?.items.map(item => item.tickets.map((ticket) => {
          if (!this.basketService.basket()?.tickets[ticket.ticketTypeId]) {
            return undefined;
          } else {
            return {
              amount: ticket.amount,
              ticketTypeId: ticket.ticketTypeId,
              slotInfo: ticket.slotInfo
            }
          }
        }).filter(Boolean)).flat()
    });
  }

  confirmCheckout(dto: ConfirmCheckoutDto) {
    if (!this.checkout()) return of(null);
    return this.http.post(`${this.appConfigService.host()}/checkout/${this.checkout()!._id}/details`, {
      secret: this.checkout()!.secret,
      ...dto
    }).pipe(
      tap(res => this.confirmCheckOutRes.set(res)),
      switchMap(() => this.doPaymentRequest())
    );
  }

  doPaymentRequest() {
    if (!this.confirmCheckOutRes()) return of(null);
    const checkoutDto: PaymentRequest = {
      checkoutId: this.checkout()!._id,
      secret: this.checkout()!.secret,
      origin: 'pos'
    };
    return this.paymentGateway.createPaymentRequest(checkoutDto);
  }

  getProcessorInfo(requestId: string) {
    return this.paymentGateway.setPaymentProcessor(
      requestId, 
      this.appConfigService.currentConfig().gatewayId
    ).pipe(
      switchMap((processor: PaymentProcessorResponse) => this.sendRequestToTerminal(requestId, processor.reference))
    );
  }

  sendRequestToTerminal(requestId: string, processorReference: string) {
    return this.paymentGateway.sendPaymentToTerminal(
      requestId,
      this.appConfigService.currentConfig().gatewayId,
      this.appConfigService.currentConfig().terminalId
    ).pipe(
      switchMap((response) => {
        this.processorId.set(response.id);
        return this.checkPaymentStatus(requestId, response.id, processorReference);
      })
    );
  }

  checkPaymentStatus(requestId: string, processorId?: string, processorReference?: string) {
    return this.paymentGateway.checkPaymentStatus(
      requestId,
      this.appConfigService.currentConfig().gatewayId,
      this.appConfigService.currentConfig().terminalId,
      processorId,
      processorReference
    ).pipe(
      tap((res: any) => {
        this.paymentStatus.next(res.status);
      })
    );
  }

  setPaymentStatus(status: PaymentStatusEnum): void {
    this.paymentStatus.next(status);
  }

  getTickets() {
    if (!this.checkout()) return of (null);
    const checkoutTickets$ = this.http.get(`${this.appConfigService.host()}/checkout/${this.checkout()!._id}/tickets?secret=${this.checkout()?.secret}`).pipe(
      map((response: any) => response.tickets)
    );
    const checkoutPayments$ = this.http.get(`${this.appConfigService.host()}/payments?checkoutId=${this.checkout()?._id}`);

    return combineLatest([checkoutTickets$, checkoutPayments$]).pipe(
      switchMap(([tickets, paymentDetails]) => {
        const transactionId = tickets[0]?.transactionId;
        if (!transactionId || transactionId === "") {
          return of({
            tickets,
            paymentDetails
          });
        }

        return this.getInvoiceByTransactionId(transactionId).pipe(
          map((response: any) => {
            return {
              tickets,
              paymentDetails,
              invoice: response.rows?.length > 0 ? response.rows[0] : null,
            };
          }),
          catchError(() => {
            return of({
              tickets,
              paymentDetails,
              invoice: null,
            });
          })
        );
      })
    );
  }

  getPaymentDetails() {
    return this.http.get(`${this.appConfigService.host()}/payments?checkoutId=${this.checkout()?._id}`);
  }

  getInvoiceByTransactionId(transactionId: string) {
    const params = {
      top: 1,
      transactionId
    };
    return this.http.get(`${this.appConfigService.host()}/invoices`, { params });
  }

  getCheckout() {
    return this.http.get(`${this.appConfigService.host()}/checkout/${this.checkout()!._id}?secret=${this.checkout()?.secret}`);
  }

  abortCurrentCheckout() {
    const checkout = this.checkout();
    if (!checkout) return of(null);
    return this.http.post(`${this.appConfigService.host()}/checkout/${checkout._id}/abort`, { secret: checkout.secret }).pipe(
      tap(() => this.checkout.set(null))
    );
  }

  removeTicketFromCheckout(ticketTypeId: string, slotId: string) {
    if (!this.checkout()) return of(null);
    const currentCheckout = this.checkout();
    const dto = this.buildUpdateCheckoutItemsDto(currentCheckout, ticketTypeId, slotId);
    //preserve applied coupons
    if (currentCheckout.items[0].appliedCoupons?.length > 0) {
      dto.coupons = currentCheckout.items[0].appliedCoupons.map(coupon => coupon.code);
    }
    return this.http.post<Checkout>(`${this.appConfigService.host()}/checkout/${currentCheckout._id}/items`, dto).pipe(
      tap(updatedCheckout => this.checkout.set(updatedCheckout))
    );
  }

  updateCheckoutVouchers(voucherCodes: string[]) {
    const dto: UpdateCheckoutVouchersDto = {
      secret: this.checkout()?.secret,
      vouchers: voucherCodes,
    };
    return this.http.put<Checkout>(`${this.appConfigService.host()}/checkout/${this.checkout()!._id}`, dto).pipe(
      tap(updatedCheckout => this.checkout.set(updatedCheckout))
    );
  }

  updateCheckoutCoupons(couponCodes: string[]) {
    if (!this.checkout()) return of(null);
    const dto: UpdateCheckoutItemsDto = {
      secret: this.checkout()?.secret,
      eventId: this.checkout()?.items[0].eventId,
      shopId: this.checkout()?.items[0].shopId,
      items: this.checkout()?.items.map(item => item.tickets.map(ticket => ({
        amount: ticket.amount,
        ticketTypeId: ticket.ticketTypeId,
        slotInfo: ticket.slotInfo
      }))).flat(),
      coupons: couponCodes,
    };

    return this.http.post<Checkout>(`${this.appConfigService.host()}/checkout/${this.checkout()!._id}/items`, dto).pipe(
      tap(updatedCheckout => this.checkout.set(updatedCheckout))
    );
  }

  private buildUpdateCheckoutItemsDto(checkout: Checkout, ticketTypeId: string, slotId: string): UpdateCheckoutItemsDto {
    const checkoutTickets: CheckoutPostItem[] = [];

    let checkoutItems = checkout.items.flatMap(checkoutItem => 
      checkoutItem.tickets.map(ticket => ({
        amount: ticket.amount,
        ticketTypeId: ticket.ticketTypeId,
        slotInfo: ticket.slotInfo
      }))
    );

    //remove items that are not in the basket 
    //(used to remove tickets that are not matching the rules)
    const basket = this.basketService.basket();
    const basketTicketIds = basket?.tickets[slotId] ? basket.tickets[slotId].map(ticket => ticket.ticket.id) : [];
    if (basketTicketIds.length > 0) {
      checkoutItems = checkoutItems.filter(item => basketTicketIds.includes(item.ticketTypeId));
    }

    checkoutItems.forEach(item => {
      if (item.ticketTypeId === ticketTypeId && (!item.slotInfo || item.slotInfo.slotId === slotId)) {
        if (item.amount > 1) {
          //if amount is greater than 1, decrease it and add the modified item
          checkoutTickets.push({ ...item, amount: item.amount - 1 });
        }
        //if amount is 1, the item is removed
      } else {
        //item does not match, so add it to the array
        checkoutTickets.push(item);
      }
    });

    return {
      secret: checkout.secret,
      eventId: checkout.items[0].eventId,
      shopId: checkout.items[0].shopId,
      items: checkoutTickets
    };
  }
}
